import { useContext } from "react";
import PropTypes from "prop-types";

import { CartContext } from "@context/CartProvider";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "@hooks/useDiscountsInfo";
import useDeviceDetect from "@hooks/useDeviceDetect";
import useTieredDiscounts from "@hooks/useTieredDiscounts";

import { applyCartDiscounts } from "@components/cartDiscounts";

import { cn } from "@helpers/general";
import { ComponentType } from "@constants/ComponentType";
import COLORS from "@constants/Colors";
import DISCOUNT_TYPES from "@constants/DiscountTypes";
import ProgressText from "@components/ProgressText";

function TieredOfferProgressBarGift({ componentType, saleColors, eligibleAmount }) {
  const { cart } = useContext(CartContext);

  const { isMobile } = useDeviceDetect();
  const discountInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.CURRENT);
  const tieredDiscount = useTieredDiscounts();

  const giftInfo = discountInfo.find(({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE);

  const sideCartInfo = applyCartDiscounts(cart.lines, discountInfo);

  if (!tieredDiscount.discounts) {
    return null;
  }

  const backgroundColorReached = saleColors?.tierBgColorLuxeReached ?? COLORS.POPPY;
  const borderColor = saleColors?.tierBorderColorLuxe ?? COLORS.BLACK;
  const textColor = saleColors?.tierTextColorLuxe ?? COLORS.BLACK;
  const textColorReached = saleColors?.tierTextColorLuxeReached ?? COLORS.WHITE;

  const firstTierReached = eligibleAmount >= tieredDiscount.discounts[0][0];
  const lastTierReached =
    tieredDiscount &&
    eligibleAmount >= tieredDiscount.discounts[tieredDiscount.discounts.length - 1][0];

  const progress = tieredDiscount.discounts.map(([threshold, discount], index) => {
    const prevThreshold = index === 0 ? 0 : tieredDiscount.discounts[index - 1][0];

    const isFilled = eligibleAmount >= threshold;
    const freeShippingText =
      index === 0 && eligibleAmount >= tieredDiscount.discounts[0][0] ? "+ Free Shipping" : "";

    const freeGiftText = index === 1 || index === 2 ? "+ Free Gift" : "";
    const subContent = [freeShippingText, freeGiftText].filter(Boolean).join(" ");

    let filledPercentage = 0;
    if (isFilled) {
      // Tier fully reached
      filledPercentage = 100;
    } else if (eligibleAmount >= prevThreshold) {
      // Between previous threshold and current one – fill proportionally
      const range = threshold - prevThreshold;
      const progressInRange = eligibleAmount - prevThreshold;
      filledPercentage = Math.min((progressInRange / range) * 100, 100);
    } else {
      // Haven't reached previous tier – keep this at 0
      filledPercentage = 0;
    }

    let additionalText = "";
    let thresholdText = threshold;
    if (tieredDiscount.type === DISCOUNT_TYPES.QUANTITY_PERCENT) {
      additionalText = threshold === 1 ? "Item" : "Items";
      if (index === 2) {
        thresholdText = `${threshold}+`;
      }
    }
    const mainContent = `${tieredDiscount.wording}${thresholdText} ${additionalText}`;
    return {
      threshold,
      isFilled,
      mainContent,
      content: `${discount}% Off`,
      subContent,
      filledPercentage,
    };
  });

  let showProgressText = false;
  if (giftInfo?.freegift?.minAmount) {
    if (
      componentType === ComponentType.SIDE_CART ||
      (componentType === ComponentType.ANNOUNCEMENT_BAR && isMobile)
    ) {
      showProgressText = true;
    }
  }

  return (
    <div
      className={cn(componentType === ComponentType.PDP && "pb-[15px] desktop:pb-[25px]")}
      style={{ color: textColor }}
    >
      {showProgressText && (
        <ProgressText
          firstTierReached={firstTierReached}
          lastTierReached={lastTierReached}
          freeGiftMinAmount={giftInfo?.freegift?.minAmount}
          freeGiftDiscount={sideCartInfo.discountFreeGift}
          content=""
          componentType={componentType}
        />
      )}
      <div className="gap-[5px] desktop:gap-[10px]" style={{ display: "flex", width: "100%" }}>
        {progress.map((item) => (
          <div key={`progress-tiered-gift-${item.threshold}`} className="flex-1 flex-wrap  min-w-0">
            <div
              className="flex flex-col items-center relative h-[7px] rounded-[8px] mb-[5px] duration-300 transition [transition-property:background-color,color,width] [transition-timing-function:ease]"
              style={{
                backgroundColor: item.isFilled ? backgroundColorReached : "",
                border: `1px solid ${borderColor}`,
              }}
            >
              {item.isFilled ? (
                <>&nbsp;</>
              ) : (
                <span
                  className="absolute left-0 h-[5px] rounded-[8px]"
                  style={{
                    width: `${item.filledPercentage}%`,
                    backgroundColor: backgroundColorReached,
                  }}
                >
                  &nbsp;
                </span>
              )}
            </div>
            <div
              className={cn(
                "leading-[0]",
                componentType === ComponentType.ANNOUNCEMENT_BAR && !isMobile
                  ? "text-center"
                  : "text-left"
              )}
              style={{
                color: item.isFilled ? textColor : textColorReached,
              }}
            >
              <div className="typo-micro whitespace-break-spaces">
                <span className="whitespace-nowrap">{item.mainContent}&nbsp;/&nbsp;</span>
                <span className={cn("bold", item.subContent && "whitespace-nowrap")}>
                  {item.content}
                </span>{" "}
                <span className="bold whitespace-nowrap">{item.subContent}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TieredOfferProgressBarGift;

TieredOfferProgressBarGift.propTypes = {
  componentType: PropTypes.oneOf(Object.values(ComponentType)).isRequired,
  saleColors: PropTypes.shape({
    tierTextColorLuxe: PropTypes.string,
    tierTextColorLuxeReached: PropTypes.string,
    tierBgColorLuxe: PropTypes.string,
    tierBgColorLuxeReached: PropTypes.string,
    tierBorderColorLuxe: PropTypes.string,
  }),
  eligibleAmount: PropTypes.number.isRequired,
};
